import { supabase } from './supabase'

// Cross-domain SSO hand-off (Navo24 host → schedulesmcp.com). The host mints a SINGLE-USE, 60-second
// nonce (mint_sso_handoff RPC on the shared Supabase) that maps to its session, and passes ONLY the
// nonce in the URL fragment (`#sso=`). We redeem it exactly once here (redeem_sso_handoff is
// delete-on-read) to get the tokens, set the session, and strip the nonce from the URL. Because the URL
// carries no token — just an unguessable nonce that dies on first use or after 60s — a copied, shared,
// or history-recovered link CANNOT be replayed on another machine. A fragment (not a query) also never
// reaches a server or an access log. No-op when there's no nonce; an invalid/expired/used one just
// falls through to normal sign-in.
const PARAM = 'sso'

export async function adoptSessionFromUrl(): Promise<void> {
  if (typeof window === 'undefined') return
  const hash = window.location.hash
  if (!hash.includes(`${PARAM}=`)) return

  const params = new URLSearchParams(hash.replace(/^#/, ''))
  const nonce = params.get(PARAM)

  // Strip the nonce from the URL first — always — so it never lingers in the bar/history.
  params.delete(PARAM)
  const rest = params.toString()
  try {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + (rest ? `#${rest}` : '')
    )
  } catch {
    /* history blocked — nonce is still redeemed below */
  }

  if (!nonce) return
  try {
    const { data, error } = await supabase.rpc('redeem_sso_handoff', { p_nonce: nonce })
    const row = Array.isArray(data) ? data[0] : null
    if (!error && row?.access_token && row?.refresh_token) {
      await supabase.auth.setSession({
        access_token: row.access_token,
        refresh_token: row.refresh_token,
      })
    }
  } catch {
    /* invalid / expired / already-used nonce — no session adopted; user signs in normally */
  }
}
